/**
 * Shared main-shell layout: compact top HUD + bottom nav.
 * Chrome from temporary tilesheet UI kit + Phosphor icons — real save data only.
 * Visible on Town / Hero / Talents / Quests / Settings only — not combat.
 */

import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../config';
import {
  CLASS_LEVEL_CAP,
  createDefaultPermanentSave,
  type PermanentSave,
} from '../../persistence/schema';
import { createSaveStore } from '../../persistence/saveStore';
import type { ShellTab } from './shellNav';
import { SHELL_TAB_SCENES } from './shellNav';
import {
  addUiPanel,
  addUiProgressBar,
  addUiTabFrame,
  addKitNineSlice,
} from './kit';
import { KIT_STATES } from './kit/manifest';
import { addGameIcon } from './icons';
import type { IconId } from './icons';
import { NAV_TAB_ICONS } from './icons';
import { addPixelText } from './pixelText';
import { UI } from './theme';

export type { ShellTab };
export { SHELL_TAB_SCENES };

/** Dynamic Island / notch clearance inside design canvas. */
export const SAFE_TOP = 36;
/** Home-indicator clearance inside design canvas. */
export const SAFE_BOTTOM = 24;

/** Compact HUD strip height (below safe top). */
export const HUD_BODY = 60;
/** Bottom nav tab strip height (above safe bottom). */
export const NAV_BODY = 62;

export const HUD_HEIGHT = SAFE_TOP + HUD_BODY;
export const NAV_HEIGHT = NAV_BODY + SAFE_BOTTOM;

/** Main content band between HUD and nav — no horizontal scroll. */
export function contentBounds(): {
  top: number;
  bottom: number;
  height: number;
  width: number;
  cx: number;
} {
  const top = HUD_HEIGHT + 4;
  const bottom = GAME_HEIGHT - NAV_HEIGHT - 4;
  return {
    top,
    bottom,
    height: bottom - top,
    width: GAME_WIDTH,
    cx: GAME_WIDTH / 2,
  };
}

const TAB_DEFS: readonly {
  id: ShellTab;
  label: string;
  icon: IconId;
}[] = [
  { id: 'hero', label: 'Hero', icon: NAV_TAB_ICONS.hero },
  { id: 'talents', label: 'Talents', icon: NAV_TAB_ICONS.talents },
  { id: 'town', label: 'Town', icon: NAV_TAB_ICONS.town },
  { id: 'quests', label: 'Quests', icon: NAV_TAB_ICONS.quests },
  { id: 'settings', label: 'Settings', icon: NAV_TAB_ICONS.settings },
];

function classDisplayName(permanent: PermanentSave): string {
  return permanent.profile.activeClassId.toUpperCase();
}

/**
 * Compact top HUD from actual PermanentSave values.
 * Gold / materials only — never invent gems or fake progression numbers.
 */
export function addTopHud(
  scene: Phaser.Scene,
  options: { onSettings?: () => void } = {},
): Phaser.GameObjects.Container {
  const permanent =
    createSaveStore().load()?.permanent ?? createDefaultPermanentSave();
  const cls = permanent.classes[permanent.profile.activeClassId];
  const barY = SAFE_TOP + HUD_BODY / 2;
  const barH = HUD_BODY - 6;
  const barW = GAME_WIDTH - 12;
  const barX = 6;

  const children: Phaser.GameObjects.GameObject[] = [];

  children.push(addUiPanel(scene, barX, barY - barH / 2, barW, barH, 'stone'));

  // Portrait frame + Phosphor user placeholder
  const avX = 14;
  const avY = barY - 18;
  children.push(
    addKitNineSlice(scene, KIT_STATES.panel.stoneDark, avX, avY, 36, 36),
  );
  children.push(
    addGameIcon(scene, 'hud.portrait', avX + 18, avY + 18, {
      size: 22,
      color: UI.parchment,
    }),
  );

  children.push(
    addGameIcon(scene, 'hud.class', 58, barY - 14, {
      size: 12,
      color: UI.goldBright,
      originX: 0,
      originY: 0.5,
    }),
  );
  children.push(
    addPixelText(scene, 74, barY - 20, classDisplayName(permanent), {
      font: 'ui-yellow',
      size: 15,
    }),
  );
  children.push(
    addGameIcon(scene, 'hud.general', 58, barY + 2, {
      size: 11,
      color: UI.ironLight,
      originX: 0,
      originY: 0.5,
    }),
  );
  children.push(
    addPixelText(
      scene,
      74,
      barY - 2,
      `Class Lv ${cls.level} · Gen Lv ${permanent.general.level}`,
      { font: 'ui-grey', size: 12 },
    ),
  );

  const xpTrackW = 120;
  const fillRatio = Math.min(1, Math.max(0, cls.level / CLASS_LEVEL_CAP));
  children.push(
    addUiProgressBar(scene, 58, barY + 14, xpTrackW, 10, fillRatio, 'green'),
  );
  children.push(
    addPixelText(
      scene,
      58 + xpTrackW + 4,
      barY + 13,
      `XP ${cls.xp}/${permanent.general.xp}`,
      { font: 'ui-grey', size: 8 },
    ),
  );

  if (permanent.currency) {
    children.push(
      addGameIcon(scene, 'hud.gold', GAME_WIDTH - 78, barY - 10, {
        size: 16,
        color: UI.goldBright,
      }),
    );
    children.push(
      addGameIcon(scene, 'hud.materials', GAME_WIDTH - 78, barY + 12, {
        size: 15,
        color: UI.amethyst,
      }),
    );
    children.push(
      addPixelText(
        scene,
        GAME_WIDTH - 68,
        barY - 16,
        String(permanent.currency.gold),
        { font: 'ui-yellow', size: 12 },
      ),
    );
    children.push(
      addPixelText(
        scene,
        GAME_WIDTH - 68,
        barY + 6,
        String(permanent.currency.materials),
        { font: 'ui-brown', size: 11 },
      ),
    );
  }

  children.push(
    addKitNineSlice(
      scene,
      KIT_STATES.slot.normal,
      GAME_WIDTH - 39,
      barY - 15,
      30,
      30,
    ),
  );
  children.push(
    addGameIcon(scene, 'hud.settings', GAME_WIDTH - 24, barY, {
      size: 18,
      color: 0xd0e0f0,
    }),
  );
  const gearHit = scene.add
    .rectangle(GAME_WIDTH - 24, barY, 44, 44, UI.bgStone, 0.001)
    .setInteractive({ useHandCursor: true });
  gearHit.on('pointerup', () => {
    if (options.onSettings) {
      options.onSettings();
    } else {
      scene.scene.start('Settings');
    }
  });
  children.push(gearHit);

  return scene.add.container(0, 0, children).setDepth(1000);
}

/**
 * Bottom nav — five equal touch targets (≥44px). Selected tab uses crest overlay.
 * Town remains a normal tab; default entry scene is still Village/Town.
 */
export function addBottomNav(
  scene: Phaser.Scene,
  selected: ShellTab,
): Phaser.GameObjects.Container {
  const barTop = GAME_HEIGHT - NAV_HEIGHT;
  const tabW = GAME_WIDTH / TAB_DEFS.length;
  const cy = barTop + NAV_BODY / 2;
  const barH = NAV_BODY - 4;

  const children: Phaser.GameObjects.GameObject[] = [];
  children.push(addUiPanel(scene, 4, barTop + 2, GAME_WIDTH - 8, barH, 'stoneDark'));

  children.push(
    scene.add
      .rectangle(
        GAME_WIDTH / 2,
        GAME_HEIGHT - SAFE_BOTTOM / 2,
        96,
        4,
        UI.ironLight,
        0.7,
      )
      .setOrigin(0.5),
  );

  TAB_DEFS.forEach((tab, i) => {
    const x = tabW * i + tabW / 2;
    const active = tab.id === selected;
    const tw = Math.max(44, tabW - 8);
    const th = Math.max(44, NAV_BODY - 16);

    children.push(addUiTabFrame(scene, x, cy, tw, th, active));
    children.push(
      addGameIcon(scene, tab.icon, x, cy - 8, {
        size: 20,
        color: active ? UI.goldBright : 0xc0b8d0,
      }),
    );

    const hit = scene.add
      .rectangle(x, cy, tw, th, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    const label = addPixelText(scene, x, cy + 10, tab.label, {
      font: active ? 'ui-yellow' : 'ui-grey',
      size: 11,
      originX: 0.5,
      originY: 0,
    });

    hit.on('pointerup', () => {
      if (tab.id === selected) {
        return;
      }
      scene.scene.start(SHELL_TAB_SCENES[tab.id]);
    });
    children.push(hit, label);
  });

  return scene.add.container(0, 0, children).setDepth(1001);
}

/** Atmospheric shell backdrop (gradient bands — kit dark fantasy). */
export function paintShellBackdrop(
  scene: Phaser.Scene,
  color = UI.bgDeep,
): void {
  const g = scene.add.graphics();
  g.fillStyle(color, 1);
  g.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
  g.fillStyle(0x1a1428, 0.55);
  g.fillRect(0, 0, GAME_WIDTH, HUD_HEIGHT + 20);
  g.fillStyle(0x120e1a, 0.7);
  g.fillRect(0, GAME_HEIGHT - NAV_HEIGHT - 10, GAME_WIDTH, NAV_HEIGHT + 10);
  g.fillStyle(0x2a1830, 0.25);
  g.fillEllipse(GAME_WIDTH / 2, GAME_HEIGHT * 0.45, GAME_WIDTH * 0.9, 220);
}

/**
 * Mount HUD + nav on a main shell scene. Returns content bounds.
 */
export function mountShell(
  scene: Phaser.Scene,
  selected: ShellTab,
): ReturnType<typeof contentBounds> {
  addTopHud(scene);
  addBottomNav(scene, selected);
  return contentBounds();
}
